import { User } from '@/core/api/entities/user'
import { store } from '@/core/store'

// Permission constants
// These constants should be used instead of string literals to avoid typos
export const PERMISSIONS = {
	// Music permissions
	FEEDBACK_SONG: 'feedback_song',
	UPSERT_FEEDBACK_SONG: 'upsert_feedback_song',
	RATE_RADIO: 'rate_radio',
	UPDATE_RADIOS: 'update_radios',
	LIST_SCHEDULES: 'list_schedules',
	CREATE_SCHEDULES: 'create_schedules',
	UPDATE_SCHEDULES: 'update_schedules',
	DESTROY_SCHEDULES: 'destroy_schedules',
	LIST_PLAYLISTS: 'list_playlists',
	CREATE_PLAYLISTS: 'create_playlists',
	UPDATE_PLAYLISTS: 'update_playlists',
	DESTROY_PLAYLISTS: 'destroy_playlists',

	// Zones permissions
	CREATE_USER_PLAYERS: 'create_user_players',
	UPDATE_USER_PLAYERS: 'update_user_players',
	DESTROY_USER_PLAYERS: 'destroy_user_players',
	UPDATE_PLAYERS_FLAG_PAUSE_SONG: 'update_players_flag_pause_song',
	UPDATE_PLAYERS_FLAG_SKIP_SONG: 'update_players_flag_skip_song',
	REFRESH_PLAYER_PIN: 'refresh_player_pin',
	DOWNLOAD_REPORT_LAST_WEEK: 'download_report_last_week',
	DOWNLOAD_REPORT_WEEK_HOURS: 'download_report_week_hours',
	VIEW_PLAYER_EVENTS: 'view_player_events',
	LIST_USER_PLAYERS: 'list_user_players',

	// Locations permissions
	LIST_USER_STORES: 'list_user_stores',
	CREATE_USER_STORES: 'create_user_stores',
	UPDATE_USER_STORES: 'update_user_stores',
	DESTROY_USER_STORES: 'destroy_user_stores',

	// Groups permissions
	CREATE_GROUPS: 'create_groups',
	UPDATE_GROUPS: 'update_groups',
	DESTROY_GROUPS: 'destroy_groups',
	LIST_GROUPS: 'list_groups',

	// Create/update/delete users
	CREATE_USERS: 'create_users',
	UPDATE_USERS: 'update_users',
	DESTROY_USERS: 'destroy_users',

	// Announcements & adverts permissions
	CREATE_ANNOUNCEMENTS: 'create_announcements',
	UPDATE_ANNOUNCEMENTS: 'update_announcements',
	DESTROY_ANNOUNCEMENTS: 'destroy_announcements',
	LIST_ANNOUNCEMENTS: 'list_announcements',
	LIST_ADVERTS: 'list_adverts',

	// Account & users
	UPDATE_ACCOUNT_DATA: 'update_account_data',
	LOGIN_AS_CHILD: 'login_as_child',
	SEE_CHILDREN_ACCOUNTS: 'see_children_accounts',
	LIST_ACCOUNTS: 'list_accounts',
	// Invoices & Payments
	DESTROY_PAYMENT_TOKENS: 'destroy_payment_tokens',
	// Music profiles
	CREATE_MUSIC_PROFILES: 'create_music_profiles',

	// Greater major permissions
	MANAGE_CHILDREN_ACCOUNTS: 'manage_children_accounts',
	MANAGE_CHILDREN_ANNOUNCEMENTS: 'manage_children_announcements',
	MANAGE_CHILDREN_GROUPS: 'manage_children_groups',
	MANAGE_CHILDREN_INVOICES: 'manage_children_invoices',
	MANAGE_CHILDREN_PLAYERS: 'manage_children_players',
	MANAGE_CHILDREN_USERS: 'manage_children_users',
	MANAGE_CHILDREN_SUBSCRIPTIONS: 'manage_children_subscriptions',
	MANAGE_CHILDREN_MUSIC_PROFILES: 'manage_children_music_profiles',
	MANAGE_CHILDREN_SCHEDULES: 'manage_children_schedules',
	MANAGE_CHILDREN_PLAYLISTS: 'manage_children_playlists',

	// Major permissions
	MANAGE_OWN_LEVEL_ANNOUNCEMENTS: 'manage_own_level_announcements',
	MANAGE_OWN_LEVEL_GROUPS: 'manage_own_level_groups',
	MANAGE_OWN_LEVEL_INVOICES: 'manage_own_level_invoices',
	MANAGE_OWN_LEVEL_PLAYERS: 'manage_own_level_players',
	MANAGE_OWN_LEVEL_SCHEDULES: 'manage_own_level_schedules',
	MANAGE_OWN_LEVEL_USERS: 'manage_own_level_users',
	MANAGE_OWN_LEVEL_MUSIC_PROFILES: 'manage_own_level_music_profiles',

	// Internal (Brandtrack) permissions

	// Other permissions
	FLAG_CAN_RESET_BOXES: 'can_reset_boxes',
}

const permissionService = {
	/**
	 * Get the current user from Redux store
	 * @returns The current user or null if not authenticated
	 */
	getCurrentUser(): User | null {
		return store.getState().auth.user
	},

	/**
	 * Check if the specified user has a permission
	 * @param user - The user to check permissions for
	 * @param permission - The permission to check
	 * @returns Boolean indicating if the user has the permission
	 */
	hasPermission(user: User | null, permission: string): boolean {
		if (!user || !user.permissions) {
			return false
		}

		return user.permissions.includes(permission)
	},

	/**
	 * Check if the current user has a permission
	 * @param permission - The permission to check
	 * @returns Boolean indicating if the current user has the permission
	 */
	currentUserHasPermission(permission: string): boolean {
		const currentUser = this.getCurrentUser()
		return this.hasPermission(currentUser, permission)
	},

	/**
	 * Check if the specified user has all of the given permissions
	 * @param user - The user to check permissions for
	 * @param permissions - Array of permissions to check
	 * @returns Boolean indicating if the user has all permissions
	 */
	hasAllPermissions(user: User | null, permissions: string[]): boolean {
		if (!user || !user.permissions) {
			return false
		}

		return permissions.every((permission) =>
			user.permissions?.includes(permission)
		)
	},

	/**
	 * Check if the current user has all of the given permissions
	 * @param permissions - Array of permissions to check
	 * @returns Boolean indicating if the current user has all permissions
	 */
	currentUserHasAllPermissions(permissions: string[]): boolean {
		const currentUser = this.getCurrentUser()
		return this.hasAllPermissions(currentUser, permissions)
	},

	/**
	 * Check if the specified user has any of the given permissions
	 * @param user - The user to check permissions for
	 * @param permissions - Array of permissions to check
	 * @returns Boolean indicating if the user has any of the permissions
	 */
	hasAnyPermission(user: User | null, permissions: string[]): boolean {
		if (!user || !user.permissions) {
			return false
		}

		return permissions.some((permission) =>
			user.permissions?.includes(permission)
		)
	},

	/**
	 * Check if the current user has any of the given permissions
	 * @param permissions - Array of permissions to check
	 * @returns Boolean indicating if the current user has any of the permissions
	 */
	currentUserHasAnyPermission(permissions: string[]): boolean {
		const currentUser = this.getCurrentUser()
		return this.hasAnyPermission(currentUser, permissions)
	},

	/**
	 * Check if the user can access the account selector
	 *
	 * @param user - The user to check access for
	 * @returns Boolean indicating if the user can access the account selector
	 */
	canAccessAccountSelector(user: User | null): boolean {
		// No user, no access
		if (!user) {
			return false
		}

		// Allow if the user can list all accounts
		if (this.hasAnyPermission(user, [PERMISSIONS.LIST_ACCOUNTS])) {
			return true
		}

		// Allow if the user can manage children accounts and is not a reseller subaccount
		if (this.hasAnyPermission(user, [PERMISSIONS.MANAGE_CHILDREN_ACCOUNTS])) {
			return true
		}

		// No access
		return false
	},
}

export default permissionService
